import type { RefObject } from "react";
import type { EmbeddedPodletResponse } from "./types.ts";
import { usePodletContent } from "./usePodletContent.ts";
import { useReloadPodlet } from "./useReloadPodlet.ts";

type EmbeddedPodletProps = {
  cautiouslyReloadScriptOnHydrationError?: boolean;
  className?: string;
  response: EmbeddedPodletResponse | "";
  suppressHydrationWarning?: boolean;
  ref?: RefObject<HTMLDivElement | null>;
};

type EmbeddedPodletInnerProps = Omit<EmbeddedPodletProps, "response"> & {
  response: EmbeddedPodletResponse;
};

const EmbeddedPodletInner = ({
  cautiouslyReloadScriptOnHydrationError = false,
  className,
  response,
  suppressHydrationWarning = false,
  ref,
}: EmbeddedPodletInnerProps) => {
  const { innerHTML, isHydrationErrorRerendering, podletId, setPodletRef } = usePodletContent(response);
  const { containerRef } = useReloadPodlet(response.js, cautiouslyReloadScriptOnHydrationError);

  return (
    <div
      className={className}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: we trust our podlets
      dangerouslySetInnerHTML={innerHTML}
      id={podletId}
      ref={(element) => {
        if (ref?.current) {
          element = ref.current;
        }
        setPodletRef(element);
        if (isHydrationErrorRerendering) {
          containerRef(element);
        }
      }}
      // This is a hack to suppress the React hydration warning that is
      // caused by the <template shadowrootmode="open"> element that is
      // used by the podlet. The issues are related to the fact that
      // <template shadowrootmode="open"> gets removed by supported
      // browsers when the HTML is parsed, causing React to complain
      // about mismatching DOM structures between the server and the
      // client. Works only in development mode.
      // See
      //   https://github.com/mfreed7/declarative-shadow-dom#-behavior
      suppressHydrationWarning={suppressHydrationWarning}
    ></div>
  );
};

export const EmbeddedPodlet = (props: EmbeddedPodletProps) => {
  const { response, ref, ...rest } = props;

  if (!response || !response.content) {
    return null;
  }

  return <EmbeddedPodletInner {...(rest as Omit<EmbeddedPodletProps, "response">)} response={response} ref={ref} />;
};
